import { useEffect, useState } from 'react'
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Input,
  KolomForm,
  PageHeader,
  Select,
  Textarea,
  useToast,
  type OpsiSelect,
} from '@/components'
import { api, pesanKesalahan } from '@/lib/api'
import type { Organisasi } from '@/lib/types'
import { useApi, usePaged } from '@/lib/useApi'

/**
 * Pengaturan yayasan: profil organisasi, model entitas (SINGLE_ENTITY /
 * MULTI_ENTITY), kebijakan pengakuan pendapatan, dan ambang batas peringatan
 * anggaran (disimpan sebagai kunci pada accounting_settings).
 */
export default function Pengaturan() {
  const toast = useToast()
  const organisasi = useApi<Organisasi>('/organizations/current')
  const pengaturan = usePaged<Record<string, unknown>>('/settings', { perHalaman: 200 })

  const [form, setForm] = useState({
    name: '',
    shortName: '',
    legalEntityType: 'FOUNDATION_WITH_UNITS',
    reportingModel: 'SINGLE_ENTITY',
    npwp: '',
    address: '',
    phone: '',
    email: '',
    website: '',
    timezone: 'Asia/Jakarta',
    currency: 'IDR',
  })
  const [memuat, setMemuat] = useState(false)

  useEffect(() => {
    const o = organisasi.data
    if (!o) return
    setForm({
      name: o.name ?? '',
      shortName: o.shortName ?? '',
      legalEntityType: o.legalEntityType ?? 'FOUNDATION_WITH_UNITS',
      reportingModel: o.reportingModel ?? 'SINGLE_ENTITY',
      npwp: o.npwp ?? '',
      address: o.address ?? '',
      phone: o.phone ?? '',
      email: o.email ?? '',
      website: o.website ?? '',
      timezone: o.timezone ?? 'Asia/Jakarta',
      currency: o.currency ?? 'IDR',
    })
  }, [organisasi.data])

  const simpanProfil = async () => {
    const id = organisasi.data?.id
    if (!id) {
      toast.gagal('Profil organisasi belum dapat dimuat.')
      return
    }
    setMemuat(true)
    try {
      await api.patch(`/organizations/${id}`, {
        name: form.name,
        shortName: form.shortName || undefined,
        legalEntityType: form.legalEntityType,
        reportingModel: form.reportingModel,
        npwp: form.npwp || undefined,
        address: form.address || undefined,
        phone: form.phone || undefined,
        email: form.email || undefined,
        website: form.website || undefined,
        timezone: form.timezone,
        currency: form.currency,
      })
      toast.sukses('Profil yayasan berhasil disimpan.')
      organisasi.muat()
    } catch (e) {
      toast.gagal(pesanKesalahan(e))
    } finally {
      setMemuat(false)
    }
  }

  const kunciPenting: Array<{ kunci: string; label: string; bantuan: string; opsi?: OpsiSelect[] }> = [
    {
      kunci: 'revenue.recognition_policy',
      label: 'Kebijakan Pengakuan Pendapatan',
      bantuan: 'Menentukan kapan pendapatan diakui pada jurnal.',
      opsi: [
        { value: 'ON_INVOICE', label: 'Saat Tagihan Terbit' },
        { value: 'ON_PAYMENT', label: 'Saat Pembayaran Diterima' },
        { value: 'DEFERRED', label: 'Ditangguhkan' },
      ],
    },
    {
      kunci: 'budget.warning_threshold_1',
      label: 'Ambang Peringatan Anggaran (kuning)',
      bantuan: 'Persentase serapan yang memicu peringatan pertama. Contoh: 80.',
    },
    {
      kunci: 'budget.warning_threshold_2',
      label: 'Ambang Peringatan Anggaran (merah)',
      bantuan: 'Persentase serangan yang memicu peringatan kritis. Contoh: 90.',
    },
  ]

  const nilaiKunci = (kunci: string): string => {
    const item = pengaturan.items.find((p) => String(p.settingKey ?? p.key) === kunci)
    return item ? String(item.settingValue ?? '') : ''
  }

  return (
    <div>
      <PageHeader
        judul="Pengaturan"
        deskripsi="Profil yayasan, model entitas pelaporan, kebijakan pengakuan pendapatan, dan ambang peringatan anggaran."
      />

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
        <Card>
          <CardHeader
            judul="Profil Yayasan"
            deskripsi="Data ini dipakai pada kop laporan dan dokumen resmi."
            aksi={
              <Button ukuran="kecil" varian="garis" onClick={organisasi.muat}>
                Muat ulang
              </Button>
            }
          />

          {organisasi.galat ? (
            <p className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              Gagal memuat profil: {organisasi.galat}
            </p>
          ) : null}

          <div className="mt-4 space-y-4">
            <KolomForm kolom={2}>
              <Input label="Nama Yayasan" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required />
              <Input label="Nama Singkat" value={form.shortName} onChange={(e) => setForm((f) => ({ ...f, shortName: e.target.value }))} />
              <Select
                label="Bentuk Badan Hukum"
                opsi={[
                  { value: 'SINGLE_LEGAL_ENTITY', label: 'Satu Badan Hukum' },
                  { value: 'FOUNDATION_WITH_UNITS', label: 'Yayasan dengan Unit' },
                  { value: 'SEPARATE_LEGAL_ENTITIES', label: 'Badan Hukum Terpisah' },
                ]}
                value={form.legalEntityType}
                onChange={(e) => setForm((f) => ({ ...f, legalEntityType: e.target.value }))}
              />
              <Select
                label="Model Entitas Pelaporan"
                bantuan="MULTI_ENTITY mengaktifkan konsolidasi per unit."
                opsi={[
                  { value: 'SINGLE_ENTITY', label: 'Entitas Tunggal (SINGLE_ENTITY)' },
                  { value: 'MULTI_ENTITY', label: 'Multi Entitas (MULTI_ENTITY)' },
                ]}
                value={form.reportingModel}
                onChange={(e) => setForm((f) => ({ ...f, reportingModel: e.target.value }))}
              />
              <Input label="NPWP" value={form.npwp} onChange={(e) => setForm((f) => ({ ...f, npwp: e.target.value }))} />
              <Input label="Telepon" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
              <Input label="Email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
              <Input label="Situs Web" value={form.website} onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))} />
              <Input label="Zona Waktu" value={form.timezone} onChange={(e) => setForm((f) => ({ ...f, timezone: e.target.value }))} />
              <Input label="Mata Uang" value={form.currency} onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))} />
            </KolomForm>
            <Textarea
              label="Alamat"
              value={form.address}
              onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
            />
            <div className="flex items-center gap-3">
              <Button onClick={() => void simpanProfil()} memuat={memuat}>
                Simpan Profil
              </Button>
              {form.reportingModel === 'MULTI_ENTITY' ? (
                <Badge nada="info">Konsolidasi aktif</Badge>
              ) : (
                <Badge nada="netral">Entitas tunggal</Badge>
              )}
            </div>
          </div>
        </Card>

        <div className="space-y-5">
          <Card>
            <CardHeader
              judul="Kebijakan & Ambang Batas"
              deskripsi="Disimpan pada GET /settings dan diubah dengan PUT /settings/:key."
              aksi={
                <Button ukuran="kecil" varian="garis" onClick={pengaturan.muat}>
                  Muat ulang
                </Button>
              }
            />
            {pengaturan.galat ? (
              <p className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                Gagal memuat pengaturan: {pengaturan.galat}
              </p>
            ) : null}
            <div className="mt-4 space-y-3">
              {kunciPenting.map((k) => (
                <NilaiPengaturan
                  key={k.kunci}
                  kunciPengaturan={k.kunci}
                  label={k.label}
                  bantuan={k.bantuan}
                  opsi={k.opsi}
                  nilaiAwal={nilaiKunci(k.kunci)}
                  setelah={pengaturan.muat}
                />
              ))}
            </div>
          </Card>

          <Card padat className="overflow-hidden">
            <div className="border-b border-netral-200 px-4 py-3">
              <CardHeader judul="Semua Pengaturan" deskripsi={`${pengaturan.items.length} kunci tersimpan.`} />
            </div>
            <div className="max-h-80 divide-y divide-netral-100 overflow-y-auto">
              {pengaturan.memuat ? (
                <p className="px-4 py-3 text-sm text-netral-500">Memuat…</p>
              ) : pengaturan.items.length === 0 ? (
                <p className="px-4 py-3 text-sm text-netral-500">Belum ada pengaturan tersimpan.</p>
              ) : (
                pengaturan.items.map((p, i) => (
                  <div key={String(p.settingKey ?? i)} className="flex items-start justify-between gap-3 px-4 py-2.5">
                    <div className="min-w-0">
                      <p className="font-mono text-xs text-netral-800">{String(p.settingKey ?? '')}</p>
                      <p className="text-xs text-netral-500">{String(p.category ?? 'umum')}</p>
                    </div>
                    <p className="max-w-[50%] truncate text-right text-sm text-netral-700">
                      {String(p.settingValue ?? '')}
                    </p>
                  </div>
                ))
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  )
}

function NilaiPengaturan({
  kunciPengaturan,
  label,
  bantuan,
  opsi,
  nilaiAwal,
  setelah,
}: {
  kunciPengaturan: string
  label: string
  bantuan: string
  opsi?: OpsiSelect[]
  nilaiAwal: string
  setelah: () => void
}) {
  const toast = useToast()
  const [nilai, setNilai] = useState(nilaiAwal)
  const [memuat, setMemuat] = useState(false)

  useEffect(() => {
    setNilai(nilaiAwal)
  }, [nilaiAwal])

  const simpan = async () => {
    setMemuat(true)
    try {
      await api.put(`/settings/${encodeURIComponent(kunciPengaturan)}`, { settingValue: nilai })
      toast.sukses(`Pengaturan "${label}" disimpan.`)
      setelah()
    } catch (e) {
      toast.gagal(pesanKesalahan(e))
    } finally {
      setMemuat(false)
    }
  }

  return (
    <div className="grid grid-cols-1 items-end gap-2 sm:grid-cols-[1fr_auto]">
      {opsi ? (
        <Select
          label={label}
          bantuan={bantuan}
          opsi={opsi}
          value={nilai}
          onChange={(e) => setNilai(e.target.value)}
        />
      ) : (
        <Input label={label} bantuan={bantuan} value={nilai} onChange={(e) => setNilai(e.target.value)} />
      )}
      <Button ukuran="kecil" varian="garis" memuat={memuat} disabled={nilai === nilaiAwal} onClick={() => void simpan()}>
        Simpan
      </Button>
    </div>
  )
}
